import { useState } from 'react';

import type { GridRowGroup, UseGroupedGridRowsProps, UseGroupedGridRowsReturn } from './types';

const groupRows = <T>({
    getGroupKey,
    getSummaryText,
    rows
}: UseGroupedGridRowsProps<T>): GridRowGroup<T>[] => {
    const groups = rows.reduce<GridRowGroup<T>[]>((result, item) => {
        const key = getGroupKey(item);
        const existingGroup = result.find(group => group.key === key);

        if (existingGroup) {
            existingGroup.items.push(item);
        } else {
            result.push({ items: [item], key, summaryText: '' });
        }

        return result;
    }, []);

    return groups.map(group => ({
        ...group,
        summaryText: getSummaryText(group.items)
    }));
};

const useGroupedGridRows = <T>({
    getGroupKey,
    getSummaryText,
    rows
}: UseGroupedGridRowsProps<T>): UseGroupedGridRowsReturn<T> => {
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

    const groups = groupRows({ getGroupKey, getSummaryText, rows });

    const isGroupOpen = (key: string) => openGroups[key] ?? true;

    const toggleGroup = (key: string) => {
        setOpenGroups(previous => ({ ...previous, [key]: !(previous[key] ?? true) }));
    };

    return {
        groups,
        isGroupOpen,
        toggleGroup
    };
};

export default useGroupedGridRows;
